import { GOOGLE_MAPS_API_KEY, GOOGLE_MAPS_JS_URL } from '../../../user_constant';

type MapsStatus = 'unloaded' | 'loading' | 'ready' | 'error';
type MapsErrorReason = 'missing-key' | 'load-error' | 'invalid-key';

type MapsLoadError = Error & { reason: MapsErrorReason };

let loadPromise: Promise<any> | null = null;
let status: MapsStatus = 'unloaded';
let errorReason: MapsErrorReason | null = null;

const createLoadError = (reason: MapsErrorReason): MapsLoadError => {
  const error = new Error(reason) as MapsLoadError;
  error.reason = reason;
  return error;
};

export const ensureMapsLoaded = (): Promise<any> => {
  const mapsWindow = window as typeof window & {
    google?: { maps?: any };
    gm_authFailure?: () => void;
  };

  if (mapsWindow.google?.maps && status === 'unloaded') {
    status = 'ready';
    loadPromise = Promise.resolve(mapsWindow.google.maps);
    return loadPromise;
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    if (!GOOGLE_MAPS_API_KEY) {
      status = 'error';
      errorReason = 'missing-key';
      reject(createLoadError('missing-key'));
      return;
    }

    status = 'loading';
    errorReason = null;
    mapsWindow.gm_authFailure = () => {
      status = 'error';
      errorReason = 'invalid-key';
      reject(createLoadError('invalid-key'));
    };

    const script = document.createElement('script');
    script.src = `${GOOGLE_MAPS_JS_URL}?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geocoding,marker`;
    script.async = true;
    script.defer = true;

    script.addEventListener('load', () => {
      if (errorReason) return;
      if (!mapsWindow.google?.maps) {
        status = 'error';
        errorReason = 'load-error';
        reject(createLoadError('load-error'));
        return;
      }
      status = 'ready';
      resolve(mapsWindow.google.maps);
    });

    script.addEventListener('error', () => {
      status = 'error';
      errorReason = 'load-error';
      reject(createLoadError('load-error'));
    });

    document.head.appendChild(script);
  });

  return loadPromise;
};

export const getMapsStatus = () => status;
export const getMapsError = () => errorReason;
